import { useState, useEffect, useMemo, useRef } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowRight, Lock } from "lucide-react";
import { SocialLoginButtons } from "@/components/SocialLoginButtons";
import { SlikeLogo } from "@/components/SlikeLogo";
import { authApi } from "@/api/auth";
import { NetworkError } from "@/api/network";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { handlePostLogin } from "@/utils/postLoginHandler";
import { isCmsUrl, appendTokenToCmsRedirect } from "@/utils/redirectHelpers";
import { setAuthenticated, setToken, clearAuth } from "@/store/slices/authSlice";
import { checkRedirectPermissions } from "@/utils/redirectPermissionCheck";
import { recordRedirectAttempt, LOOP_REDIRECT_QUERY_PARAM } from "@/utils/redirectLoopGuard";

const validateEmail = (emailValue: string): string | null => {
  const trimmedEmail = emailValue.trim().toLowerCase();

  if (!trimmedEmail) {
    return "Please enter your email address";
  }

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  if (!isValidEmail) {
    return "Please enter a valid email address";
  }

  if (trimmedEmail.length > 55) {
    return "Email must be at most 55 characters";
  }

  return null;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState<boolean | null>(null);
  const [emailChecked, setEmailChecked] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hasCheckedAuth = useRef(false);
  const hasHandledExternalRedirect = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, jwt, token, profile } = useAppSelector((state) => state.auth);

  const { redirectParam, serviceParam, redirectOrigin } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect") || params.get("returnUrl");
    const service = params.get("service");
    let origin = undefined;
    if (redirect) {
      try {
        const url = new URL(redirect);
        origin = url.origin;
      } catch {
        // Invalid redirect URL, origin will remain undefined
      }
    }
    return { redirectParam: redirect, serviceParam: service, redirectOrigin: origin };
  }, [location.search]);

  // Initial check on mount
  useEffect(() => {
    if (hasCheckedAuth.current) {
      return;
    }
    hasCheckedAuth.current = true;

    const checkAuthAndRedirect = async () => {
      const hasExternalRedirect = redirectParam && redirectParam.startsWith('http');
      
      if (hasExternalRedirect) {
        // If already authenticated, redirect immediately to the external URL
        if (isAuthenticated) {
          const authToken = token || jwt || '';
          
          // For cookie-based auth, we may not have a token, but we're still authenticated
          if (authToken) {
            await handlePostLogin({
              token: authToken,
              redirect: redirectParam,
              redirectOrigin: redirectOrigin,
              dispatch,
              navigate,
              showToast: false
            });
          } else {
            // Cookie-based auth - check permissions before redirecting
            const userPermissions = profile?.permissions || [];
            const { hasAccess } = checkRedirectPermissions(redirectParam, userPermissions);
            
            if (!hasAccess) {
              navigate(`/home?redirect=${encodeURIComponent(redirectParam)}`, { replace: true });
              return;
            }
            
            const { isLooping, origin } = recordRedirectAttempt(redirectParam);
            if (isLooping) {
              navigate(`/home?${LOOP_REDIRECT_QUERY_PARAM}=${encodeURIComponent(origin || redirectParam)}`, { replace: true });
              return;
            }

            // User has access - proceed with redirect
            const redirectUrl = isCmsUrl(redirectParam) && authToken
              ? appendTokenToCmsRedirect(redirectParam, authToken)
              : redirectParam;
            window.location.href = redirectUrl;
          }
          return;
        }

        // Not authenticated, stay on login page
        setIsCheckingAuth(false);
        return;
      }
      
      if (isAuthenticated) {
        navigate("/home", { replace: true });
        return;
      }
      
      setIsCheckingAuth(false);
    };
    
    checkAuthAndRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for auth state changes (when AuthGuard restores auth)
  useEffect(() => {
    const hasExternalRedirect = redirectParam && redirectParam.startsWith('http');
    
    // If we have external redirect, are now authenticated, and haven't handled it yet
    if (hasExternalRedirect && isAuthenticated && !hasHandledExternalRedirect.current) {
      hasHandledExternalRedirect.current = true;
      const authToken = token || jwt || '';
      
      // For cookie-based auth, we may not have a token
      if (authToken) {
        handlePostLogin({
          token: authToken,
          redirect: redirectParam,
          redirectOrigin: redirectOrigin,
          dispatch,
          navigate,
          showToast: false
        });
      } else {
        const userPermissions = profile?.permissions || [];
        const { hasAccess } = checkRedirectPermissions(redirectParam, userPermissions);
        
        if (!hasAccess) {
          // User doesn't have access - navigate to home with redirect param to show dialog
          navigate(`/home?redirect=${encodeURIComponent(redirectParam)}`, { replace: true });
          return;
        }
        
        const { isLooping, origin } = recordRedirectAttempt(redirectParam);
        if (isLooping) {
          navigate(`/home?${LOOP_REDIRECT_QUERY_PARAM}=${encodeURIComponent(origin || redirectParam)}`, { replace: true });
          return;
        }

        // User has access - proceed with redirect
        const redirectUrl = isCmsUrl(redirectParam) && authToken
          ? appendTokenToCmsRedirect(redirectParam, authToken)
          : redirectParam;
        window.location.href = redirectUrl;
      }
    }
  }, [isAuthenticated, token, jwt, redirectParam, redirectOrigin, dispatch, navigate, profile]);

  const handleEmailBlur = async () => {
    if (!hasAttemptedSubmit) return;

    const trimmedEmail = email.trim().toLowerCase();
    const error = validateEmail(email);

    if (error) {
      setEmailError(error);
      setTotpEnabled(null);
      setEmailChecked(false);
      return;
    }

    setEmailError(null);

    try {
      const checkResponse = await authApi.checkUser(trimmedEmail);
      if (checkResponse.data?.loginwith === "otp") {
        setTotpEnabled(checkResponse.data?.totpEnabled === true);
        setEmailChecked(true);
      } else {
        setTotpEnabled(null);
        setEmailChecked(false);
      }
    } catch {
      setTotpEnabled(null);
      setEmailChecked(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    const trimmedEmail = email.trim().toLowerCase();
    const error = validateEmail(email);

    if (error) {
      setEmailError(error);
      return;
    }

    setEmailError(null);

    setIsLoading(true);
    try {
      const checkResponse = await authApi.checkUser(trimmedEmail);

      if (checkResponse.data?.loginwith !== "otp") {
        toast.error(`Please use ${checkResponse.data?.loginwith || "the appropriate"} login method.`);
        setIsLoading(false);
        return;
      }

      const isTotpEnabled = checkResponse.data?.totpEnabled === true;

      await authApi.sendOTP(trimmedEmail);

      toast.success(isTotpEnabled ? "OTP sent. 2FA is enabled for this account." : "OTP sent to your email");
      navigate("/verify-otp", {
        state: {
          email: trimmedEmail,
          totpEnabled: isTotpEnabled,
          loginMethod: 'email',
          redirect: redirectParam,
          redirectOrigin: redirectOrigin,
          service: serviceParam
        }
      });
    } catch (error) {
      if (error instanceof NetworkError) {
        if (error.status === 404) {
          toast.error("User not found. Please sign up to create an account.");
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingAuth || isAuthenticated) {
    return (
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <SlikeLogo
              iconClassName="h-10"
              textClassName="text-4xl"
            />
          </div>
        </div>
        <Card className="glass-card p-6">
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-sm text-muted-foreground">Please wait...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md animate-fade-in">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-6">
          <SlikeLogo
            iconClassName="h-10"
            textClassName="text-4xl"
          />
        </div>
        <h1 className="text-3xl font-bold mb-2">Welcome Back</h1>
        <p className="text-muted-foreground">Sign in to your account to continue</p>
      </div>

      <Card className="glass-card p-6">
        <form onSubmit={handleSendOTP} className="space-y-6" noValidate>
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                type="text"
                inputMode="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  const val = e.target.value;
                  setEmail(val);
                  setEmailChecked(false);
                  setTotpEnabled(null);
                  if (hasAttemptedSubmit) {
                    setEmailError(validateEmail(val));
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedValue = e.clipboardData.getData("text").trim();
                  if (!pastedValue) return;

                  const truncated = pastedValue.slice(0, 55);
                  setEmail(truncated);
                  setEmailChecked(false);
                  setTotpEnabled(null);

                  if (pastedValue.length > 55) {
                    setEmailError("Email must be at most 55 characters");
                  } else if (hasAttemptedSubmit) {
                    setEmailError(validateEmail(truncated));
                  }
                }}
                onBlur={handleEmailBlur}
                className={`pl-10 ${emailError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                autoFocus
                maxLength={55}
              />
            </div>
            {emailError && (
              <p className="text-sm text-destructive mt-1 mb-1 animate-in fade-in-0 slide-in-from-top-1 px-1">
                {emailError}
              </p>
            )}
            {emailChecked && totpEnabled && !emailError && (
              <div className="flex items-center gap-2 text-xs text-primary animate-in fade-in-0 slide-in-from-top-1 mt-2">
                <Lock className="w-3 h-3" />
                <span>2FA is enabled - you'll need your authenticator app</span>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full gradient-primary hover:opacity-90"
            disabled={isLoading || !email || !!emailError || !!validateEmail(email)}
          >
            {isLoading ? (
              "Sending OTP..."
            ) : (
              <>
                Continue with Email
                {emailChecked && totpEnabled ? <Lock className="ml-2 w-4 h-4" /> : <ArrowRight className="ml-2 w-4 h-4" />}
              </>
            )}
          </Button>
        </form>

        <div className="mt-6">
          <SocialLoginButtons
            redirect={redirectParam || undefined}
            redirectOrigin={redirectOrigin}
            service={serviceParam || undefined}
          />
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/request-access")}
              className="text-primary hover:underline font-medium"
            >
              Request Access
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Login;